import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  Calendar,
  CheckSquare,
  ChevronRight,
  Edit,
  FileText,
  Loader2,
  Menu,
  Rocket,
  Ruler,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useConflictsQuery } from '~entities/recital-manager/api/get-conflicts';
import { useGetRecital } from '~entities/recital-manager/api/get-recital';
import { useGetRecitalStats } from '~entities/recital-manager/api/get-recital-stats';
import { usePublishRecitalMutation } from '~entities/recital-manager/api/publish-recital';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/shadcn/ui/alert-dialog';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/shared/shadcn/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/shared/shadcn/ui/sheet';
import { cn } from '~shared/shadcn/lib/utils';
import { formatDateIgnoreTimezone } from '~shared/utils/date-time';

import { CheckInTab } from './check-in-tab';
import { ConflictsTab } from './conflicts-tab';
import { CostumeSizingTab } from './costume-sizing-tab';
import { EditRecitalDialog } from './edit-recital-dialog';
import { RecitalStatsTab } from './recital-stats-tab';
import { ReportsTab } from './reports-tab';
import { ShowsTab } from './shows-tab';
import { VolunteersTab } from './volunteers-tab';
import {
  isRecitalDetailTab,
  RECITAL_DETAIL_TAB,
  type RecitalDetailTab,
} from './recital-details-page.types';

const RECITAL_DETAIL_NAV: Array<{
  value: RecitalDetailTab;
  label: string;
  icon: LucideIcon;
}> = [
  {
    value: RECITAL_DETAIL_TAB.SHOWS,
    label: 'Shows & Performances',
    icon: Calendar,
  },
  {
    value: RECITAL_DETAIL_TAB.CONFLICTS,
    label: 'Conflicts',
    icon: AlertTriangle,
  },
  {
    value: RECITAL_DETAIL_TAB.SIZING,
    label: 'Costume Sizing',
    icon: Ruler,
  },
  {
    value: RECITAL_DETAIL_TAB.VOLUNTEERS,
    label: 'Volunteers',
    icon: UserCheck,
  },
  {
    value: RECITAL_DETAIL_TAB.CHECK_IN,
    label: 'Check-In',
    icon: CheckSquare,
  },
  {
    value: RECITAL_DETAIL_TAB.STATS,
    label: 'Statistics',
    icon: Users,
  },
  {
    value: RECITAL_DETAIL_TAB.REPORTS,
    label: 'Reports',
    icon: FileText,
  },
];

interface RecitalDetailsPageProps {
  recitalId: string;
  tab?: string;
}

export const RecitalDetailsPage = ({
  recitalId,
  tab,
}: RecitalDetailsPageProps) => {
  const navigate = useNavigate();
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recitalMenuOpen, setRecitalMenuOpen] = useState(false);
  const { hasAdminAccess } = useCurrentUserRole();
  const { data: recital, isLoading } = useGetRecital(recitalId);
  const { data: stats } = useGetRecitalStats(recitalId);
  const { data: conflicts = [] } = useConflictsQuery({
    recitalId,
    filters: { resolved: false },
  });
  const { mutateAsync: publishRecital, isPending: isPublishingRecital } =
    usePublishRecitalMutation();

  const handlePublish = async () => {
    try {
      await publishRecital(recitalId);
      toast.success('Recital published successfully.');
      setPublishDialogOpen(false);
    } catch (error) {
      toast.error('Failed to publish recital');
      console.error(error);
    }
  };

  if (isLoading) {
    return (
      <div className='flex h-96 items-center justify-center'>
        <div className='text-lg'>Loading recital details...</div>
      </div>
    );
  }

  if (!recital) {
    return (
      <div className='flex h-96 items-center justify-center'>
        <div className='text-lg'>Recital not found</div>
      </div>
    );
  }

  const activeTab: RecitalDetailTab = isRecitalDetailTab(tab)
    ? tab
    : RECITAL_DETAIL_TAB.SHOWS;

  const navigateToTab = (value: RecitalDetailTab) => {
    setRecitalMenuOpen(false);
    void navigate({
      to: '/recitals/$recitalId',
      params: { recitalId },
      search: { tab: value },
      replace: true,
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
      draft: 'outline',
      published: 'default',
      archived: 'secondary',
    };
    return (
      <Badge variant={variants[status] ?? 'default'}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className=''>
      {/* Breadcrumbs */}
      <nav className='mb-6 flex items-center gap-2 text-sm'>
        <Link
          to='/recitals'
          className='text-muted-foreground transition-colors hover:text-foreground'
        >
          Recitals
        </Link>
        <ChevronRight className='size-4 text-muted-foreground' />
        <span className='font-medium'>{recital.name}</span>
      </nav>

      {/* Header Card */}
      <Card className='mb-6'>
        <CardHeader>
          <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
            <div className='min-w-0 flex-1'>
              <div className='mb-2 flex flex-wrap items-center gap-2 md:gap-3'>
                <CardTitle className='text-2xl capitalize md:text-4xl'>
                  {recital.name}
                </CardTitle>
                {getStatusBadge(recital.status)}
              </div>
              <CardDescription className='mt-2 text-base'>
                {recital.description?.length && recital.description.length > 0
                  ? recital.description.charAt(0).toUpperCase() +
                    recital.description.slice(1)
                  : 'No description provided'}
              </CardDescription>
            </div>
            {hasAdminAccess && (
              <div className='hidden shrink-0 gap-2 md:flex'>
                <Button
                  onClick={() => {
                    setEditDialogOpen(true);
                  }}
                  variant='outline'
                >
                  <Edit />
                  Edit Recital
                </Button>
                {recital.status === 'draft' && (
                  <Button
                    onClick={() => {
                      setPublishDialogOpen(true);
                    }}
                  >
                    <Rocket />
                    Publish Recital
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-2 gap-6 md:grid-cols-4'>
            <div>
              <div className='mb-1 text-sm text-muted-foreground'>Year</div>
              <div className='text-2xl font-bold'>{recital.year}</div>
            </div>
            <div>
              <div className='mb-1 text-sm text-muted-foreground'>
                Start Date
              </div>
              <div className='text-2xl font-bold'>
                {formatDateIgnoreTimezone(recital.startDate)}
              </div>
            </div>
            <div>
              <div className='mb-1 text-sm text-muted-foreground'>End Date</div>
              <div className='text-2xl font-bold'>
                {formatDateIgnoreTimezone(recital.endDate)}
              </div>
            </div>
            <div>
              <div className='mb-1 text-sm text-muted-foreground'>
                Total Shows
              </div>
              <div className='text-2xl font-bold'>{stats?.totalShows ?? 0}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (isRecitalDetailTab(value)) {
            navigateToTab(value);
          }
        }}
        className='w-full'
      >
        <Sheet open={recitalMenuOpen} onOpenChange={setRecitalMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant='outline'
              className='relative mb-4 flex w-full items-center justify-center gap-2 md:hidden'
            >
              <Menu className='size-4 shrink-0' />
              <span className='text-center'>Quick Actions</span>
              {activeTab === RECITAL_DETAIL_TAB.CONFLICTS &&
              conflicts.length > 0 ? (
                <Badge variant='destructive'>{conflicts.length}</Badge>
              ) : null}
            </Button>
          </SheetTrigger>
          <SheetContent side='right' className='w-[300px] overflow-y-auto'>
            <SheetHeader>
              <SheetTitle>Recital menu</SheetTitle>
              <SheetDescription>
                Edit this recital or switch to another section.
              </SheetDescription>
            </SheetHeader>
            <div className='mt-6 flex flex-col gap-3'>
              {hasAdminAccess ? (
                <>
                  <Button
                    variant='outline'
                    className='w-full justify-start'
                    onClick={() => {
                      setRecitalMenuOpen(false);
                      setEditDialogOpen(true);
                    }}
                  >
                    <Edit className='mr-2 size-4' />
                    Edit Recital
                  </Button>
                  {recital.status === 'draft' ? (
                    <Button
                      className='w-full justify-start'
                      onClick={() => {
                        setRecitalMenuOpen(false);
                        setPublishDialogOpen(true);
                      }}
                    >
                      <Rocket className='mr-2 size-4' />
                      Publish Recital
                    </Button>
                  ) : null}
                  <div className='border-t pt-3' />
                </>
              ) : null}
              {RECITAL_DETAIL_NAV.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.value;
                return (
                  <Button
                    key={item.value}
                    variant={isActive ? 'secondary' : 'outline'}
                    className={cn(
                      'h-auto w-full justify-start whitespace-normal py-2 text-left',
                      isActive && 'ring-1 ring-border',
                    )}
                    onClick={() => {
                      navigateToTab(item.value);
                    }}
                  >
                    <Icon className='mr-2 size-4 shrink-0' />
                    <span className='flex-1'>{item.label}</span>
                    {item.value === RECITAL_DETAIL_TAB.CONFLICTS &&
                    conflicts.length > 0 ? (
                      <Badge variant='destructive' className='ml-2'>
                        {conflicts.length}
                      </Badge>
                    ) : null}
                  </Button>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>

        <TabsList className='hidden h-auto w-fit max-w-full flex-wrap md:inline-flex'>
          {RECITAL_DETAIL_NAV.map((item) => {
            const Icon = item.icon;
            return (
              <TabsTrigger key={item.value} value={item.value}>
                <Icon className='mr-2 size-4 shrink-0' />
                {item.label}
                {item.value === RECITAL_DETAIL_TAB.CONFLICTS &&
                conflicts.length > 0 ? (
                  <Badge variant='destructive' className='ml-2'>
                    {conflicts.length}
                  </Badge>
                ) : null}
              </TabsTrigger>
            );
          })}
        </TabsList>
        <TabsContent value='shows' className='mt-6'>
          <ShowsTab recitalId={recitalId} />
        </TabsContent>
        <TabsContent value='conflicts' className='mt-6'>
          <ConflictsTab recitalId={recitalId} />
        </TabsContent>
        <TabsContent value='sizing' className='mt-6'>
          <CostumeSizingTab recitalId={recitalId} />
        </TabsContent>
        <TabsContent value='volunteers' className='mt-6'>
          <VolunteersTab recitalId={recitalId} />
        </TabsContent>
        <TabsContent value='check-in' className='mt-6'>
          <CheckInTab recitalId={recitalId} />
        </TabsContent>
        <TabsContent value='stats' className='mt-6'>
          <RecitalStatsTab recitalId={recitalId} stats={stats} />
        </TabsContent>
        <TabsContent value='reports' className='mt-6'>
          <ReportsTab recital={recital} />
        </TabsContent>
      </Tabs>

      {/* Publish Confirmation Dialog */}
      <AlertDialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish Recital</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to publish &quot;{recital.name}&quot;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePublish}
              disabled={isPublishingRecital}
            >
              {isPublishingRecital && <Loader2 className='animate-spin' />}
              Publish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Recital Dialog */}
      <EditRecitalDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        recitalId={recitalId}
        recital={recital}
      />
    </div>
  );
};
